import { useGetProfile } from "@workspace/api-client-react";
import {
  formatStudentName,
  isStudentDisplayMode,
  type DisplayableStudent,
  type StudentDisplayMode,
} from "@workspace/student-display";

export {
  formatStudentName,
  getEffectiveDisplayMode,
  studentSortKey,
  isStudentDisplayMode,
  STUDENT_DISPLAY_MODES,
} from "@workspace/student-display";

export type { DisplayableStudent, StudentDisplayMode } from "@workspace/student-display";

// Convenience alias used at every FE display site to avoid clashing with
// the OpenAPI-generated `Student` model name. Both shapes are structurally
// compatible — the helper only reads optional name/code/displayMode fields.
export type { DisplayableStudent as Student } from "@workspace/student-display";

/**
 * Read the user's account-level default display mode from the loose
 * `profile.preferences.defaultStudentDisplayMode` bag. Returns `undefined`
 * when unset or invalid — the helper then falls back to "full".
 */
export function useDefaultStudentDisplayMode(): StudentDisplayMode | undefined {
  const { data: profile } = useGetProfile();
  // The OpenAPI Profile type leaves preferences as an unknown JSON bag.
  const prefs = (profile as { preferences?: { defaultStudentDisplayMode?: unknown } } | undefined)
    ?.preferences;
  const raw = prefs?.defaultStudentDisplayMode;
  return isStudentDisplayMode(raw) ? raw : undefined;
}

/**
 * Convenience hook returning a memo-friendly formatter that's already
 * bound to the user's account default display mode.
 */
export function useStudentNameFormatter(): (student: DisplayableStudent | null | undefined) => string {
  const mode = useDefaultStudentDisplayMode();
  return (s) => formatStudentName(s, mode);
}
